import { Button, Card, Center, Loader, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiClock, FiCompass } from 'react-icons/fi';
import { Link, isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { PageBody, PageHeader } from './PageHeader';

/** Centered icon + title + hint + optional action; the standard empty state (doc 09 §4). */
export function EmptyState({
  icon,
  title,
  hint,
  action,
}: {
  icon: ReactNode;
  title: string;
  hint?: string;
  action?: ReactNode;
}) {
  return (
    <Card>
      <Stack align="center" gap="sm" py="xl">
        <ThemeIcon size={48} radius="xl" variant="light">
          {icon}
        </ThemeIcon>
        <Title order={3}>{title}</Title>
        {hint && (
          <Text c="dimmed" ta="center" maw={520}>
            {hint}
          </Text>
        )}
        {action}
      </Stack>
    </Card>
  );
}

export function ComingSoonPage({ titleKey }: { titleKey: string }) {
  const { t } = useTranslation();
  return (
    <>
      <PageHeader title={t(titleKey)} crumbs={[{ label: t('common.home'), to: '/' }, { label: t(titleKey) }]} />
      <PageBody>
        <EmptyState icon={<FiClock size={22} />} title={t('common.comingSoon')} hint={t('common.comingSoonHint')} />
      </PageBody>
    </>
  );
}

export function NotFoundPage({ homeHref = '/' }: { homeHref?: string }) {
  const { t } = useTranslation();
  return (
    <PageBody>
      <EmptyState
        icon={<FiCompass size={22} />}
        title={t('common.notFoundTitle')}
        hint={t('common.notFoundHint')}
        action={
          <Button component={Link} to={homeHref} variant="light">
            {t('common.backHome')}
          </Button>
        }
      />
    </PageBody>
  );
}

export function RouteErrorPage() {
  const { t } = useTranslation();
  const error = useRouteError();
  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : error instanceof Error
      ? error.message
      : undefined;
  return (
    <Center mih="100vh" p="lg">
      <EmptyState
        icon={<FiAlertTriangle size={22} />}
        title={t('common.errorTitle')}
        hint={message}
        action={
          <Button onClick={() => window.location.assign('/')} variant="light">
            {t('common.backHome')}
          </Button>
        }
      />
    </Center>
  );
}

export function FullPageLoader() {
  const { t } = useTranslation();
  return (
    <Center mih="100vh">
      <Loader aria-label={t('common.loading')} />
    </Center>
  );
}
